import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	HIGHER_ORDER_PR_STATUSES,
	PURCHASE_REQUEST_STATUSES,
	PURCHASE_REQUEST_STATUS_DISPLAY_NAMES,
} from '../../constants/PurchaseRequestStatuses';
import { Button, Card, Popover, Steps, message } from 'antd';
import { purchaseRequestStatusChangeForSupplier } from '../../thunks/purchase_requests_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const PR_ACTIONS = {
	APPROVE: 'approve',
	REJECT: 'reject',
	ORDER: 'ordered',
	ORDER_IN_PROGRESS: 'orderInProgress',
	FULFILL: 'fulfilled',
};

const PurchaseRequestStatusActions: FC<any> = ({
	purchaseRequest,
	updatePurchaseRequestStatus,
	onSuccess,
}): React.ReactElement => {
	const [currentAction, setCurrentAction] = useState(null);

	const prId = useMemo(() => nullSafeGet('id', purchaseRequest), [purchaseRequest]);

	const canShowActions = useMemo(
		() => HIGHER_ORDER_PR_STATUSES.includes(purchaseRequest.status),
		[purchaseRequest.status]
	);

	const currentPRIndex = useMemo(
		() => HIGHER_ORDER_PR_STATUSES.indexOf(purchaseRequest.status),
		[purchaseRequest.status]
	);

	const isLocationPR = useMemo(() => nullSafeGet('locationId', purchaseRequest), [purchaseRequest]);

	const markFulfilledDisabledWithAssetTypeId = useMemo(() => {
		const eqLineItems = nullSafeGetOrElse(
			'equipmentSupplierPurchaseRequestLineItems',
			purchaseRequest,
			[]
		);
		return isLocationPR && nullSafeGet('0', eqLineItems)
			? !!eqLineItems.find((_) => !nullSafeGet('equipment.assetTypeId', _))
			: false;
	}, [isLocationPR, purchaseRequest]);

	const messageHeader = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return 'This purchase request is on hold pending your approval.';
			case PURCHASE_REQUEST_STATUSES.approved:
				return 'This purchase request is approved.';
			case PURCHASE_REQUEST_STATUSES.orderInProgress:
				return 'Order is in progress for this purchase request.';
			case PURCHASE_REQUEST_STATUSES.ordered:
				return 'This purchase request is ordered.';
			case PURCHASE_REQUEST_STATUSES.fulfilled:
				return 'This purchase request is fulfilled.';
			default:
				return '';
		}
	}, [purchaseRequest.status]);

	const messageDescriptin = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return 'Please review the details below and either approve the purchase request or reject it.';
			case PURCHASE_REQUEST_STATUSES.approved:
				return 'Do you want to mark this ordered?';
			case PURCHASE_REQUEST_STATUSES.orderInProgress:
				return 'Do you want to mark this ordered or has this been fulfilled?';
			case PURCHASE_REQUEST_STATUSES.ordered:
				return 'Is this purchase request fulfilled?';
			default:
				return '';
		}
	}, [purchaseRequest.status]);

	const onApprove = useCallback(() => {
		setCurrentAction(PR_ACTIONS.APPROVE);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.approved)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setCurrentAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const onReject = useCallback(() => {
		setCurrentAction(PR_ACTIONS.REJECT);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.denied)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setCurrentAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const onOrdered = useCallback(() => {
		setCurrentAction(PR_ACTIONS.ORDER);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.ordered)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setCurrentAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const onFulfilled = useCallback(() => {
		setCurrentAction(PR_ACTIONS.FULFILL);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.fulfilled)
			.then((res) => onSuccess && onSuccess(res))
			.catch((err) => message.error(err))
			.finally(() => setCurrentAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const prActions = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return (
					<div style={{ marginTop: 16 }}>
						<Button
							type="primary"
							key="approve"
							loading={currentAction === PR_ACTIONS.APPROVE}
							onClick={onApprove}
						>
							Approve
						</Button>
						<Button
							key="reject"
							style={{ marginLeft: 16 }}
							onClick={onReject}
							loading={currentAction === PR_ACTIONS.REJECT}
						>
							Reject
						</Button>
					</div>
				);
			case PURCHASE_REQUEST_STATUSES.approved:
				return (
					<div style={{ marginTop: 16 }}>
						<Button
							type="primary"
							key="approve"
							loading={currentAction === PR_ACTIONS.ORDER}
							onClick={onOrdered}
						>
							Mark this ordered
						</Button>
						<Popover
							content={
								markFulfilledDisabledWithAssetTypeId
									? 'Please associate asset type with every equipment type in this purchase request!'
									: null
							}
							trigger="hover"
						>
							<Button
								key="reject"
								disabled={markFulfilledDisabledWithAssetTypeId}
								style={{ marginLeft: 16 }}
								loading={currentAction === PR_ACTIONS.FULFILL}
								onClick={onFulfilled}
							>
								Mark this fulfilled
							</Button>
						</Popover>
					</div>
				);
			case PURCHASE_REQUEST_STATUSES.ordered:
				return (
					<div style={{ marginTop: 16 }}>
						<Popover
							content={
								markFulfilledDisabledWithAssetTypeId
									? 'Please associate asset type with every equipment type in this purchase request!'
									: null
							}
							trigger="hover"
						>
							<Button
								type="primary"
								disabled={markFulfilledDisabledWithAssetTypeId}
								key="approve"
								loading={currentAction === PR_ACTIONS.FULFILL}
								onClick={onFulfilled}
							>
								Mark this fulfilled.
							</Button>
						</Popover>
					</div>
				);
			default:
				return null;
		}
	}, [
		currentAction,
		markFulfilledDisabledWithAssetTypeId,
		onApprove,
		onFulfilled,
		onOrdered,
		onReject,
		purchaseRequest.status,
	]);

	return canShowActions ? (
		<Card>
			<div className="flex flex-col">
				<div className="mb-4">
					<Steps current={currentPRIndex}>
						{HIGHER_ORDER_PR_STATUSES.map((status) => (
							<Steps.Step key={status} title={PURCHASE_REQUEST_STATUS_DISPLAY_NAMES[status]} />
						))}
					</Steps>
				</div>
				<h5>{messageHeader}</h5>
				<div style={{ fontSize: 16, maxWidth: 640 }}>{messageDescriptin}</div>
				<div>{prActions}</div>
			</div>
		</Card>
	) : null;
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updatePurchaseRequestStatus: (id, status) =>
		dispatch(purchaseRequestStatusChangeForSupplier(id, status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseRequestStatusActions);
